/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {ReactElement} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {GenericObject} from '@ion/core/src/types/entities';
import MaterialDropdownIcon from '../../icons/current/MaterialDropdownIcon';
import {GrabberIcon} from '../../icons/GrabberIcon';
import {ResizeGrabberPullTabIcon} from '../../icons/ResizeGrabberPullTabIcon';
import Render from '../../render/Render';
import {dlsColor} from '../../theme/theme';
import useThemeContext from '../../theme/useThemeContext';
import {DLSTheme, IconSize} from '../../types/types';

export interface ResizeGrabberProps {
  breadCrumbComponent?: ReactElement;
  isVertical?: boolean;
  hasReport?: boolean;
  width?: string;
  color?: string;
  style?: GenericObject;
};

interface StyleProps {
  isVertical?: boolean;
  width?: string
}

const useStyles = makeStyles<DLSTheme, StyleProps>(theme => ({
  grabber : {
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center',
    padding        : '5px 0',
    width          : ({isVertical, width}) => isVertical ? '100%' : width,
    cursor         : 'grab'
  },
  pullTab : {
    display    : 'flex',
    cursor     : 'grab',
    lineHeight : 0
  },
  breadCrumb : {
    position : 'absolute',
    bottom   : 5
  },
  pullTabContainer : {
    top             : '50%',
    left            : '50%',
    width           : '24px',
    cursor          : 'grab',
    height          : '70px',
    display         : 'flex',
    position        : 'absolute',
    boxShadow       : '6px 6px 10px rgba(0, 0, 0, 0.1)',
    transform       : 'translate(-50%, -50%)',
    alignItems      : 'center',
    borderRadius    : '8px',
    flexDirection   : 'column',
    justifyContent  : 'center',
    backgroundColor : dlsColor('white')
  },
  pullTabView : {
    left     : '6px',
    display  : 'flex',
    position : 'relative'
  },
  pullTabIconDown : {
    right     : '14px',
    display   : 'flex',
    position  : 'relative',
    transform : 'rotate(180deg)'
  }
}));

/**
 * Wraps the grabber icon as a grabber component with custom cursor
 * rendered when hovered over the rendered wrapper element.
 *
 * ```typescript
 *   import {ResizeGrabber} from '@ion/dls/src/components/resizable';
 *
 *   <ResizeGrabber cursor='col-resize' />
 */
export const ResizeGrabber = ({
  breadCrumbComponent,
  style,
  color,
  width = '10%',
  hasReport = false,
  isVertical = false
}: ResizeGrabberProps) => {
  const classes = useStyles({isVertical, width});

  const {
    isLegacy  : isLegacyTheme,
    isCurrent : isCurrentTheme
  } = useThemeContext();

  return (
    <>
      {breadCrumbComponent && (
        <div>
          <span className={classes.breadCrumb}>
            {breadCrumbComponent}
          </span>
        </div>
      )}
      <Render.If condition={isCurrentTheme && !hasReport}>
        <div className={classes.pullTab} style={style}>
          <ResizeGrabberPullTabIcon />
        </div>
      </Render.If>
      <Render.If condition={isCurrentTheme && hasReport}>
        <div className={classes.pullTabContainer}>
          <div className={classes.pullTabView}>
            <MaterialDropdownIcon size={IconSize.medium}
              color={dlsColor('grey1')} />
            <div className={classes.pullTabIconDown}>
              <MaterialDropdownIcon size={IconSize.medium}
                color={dlsColor('grey1')} />
            </div>
          </div>
        </div>
      </Render.If>
      <Render.If condition={isLegacyTheme}>
        <div className={classes.grabber} style={style}>
          <GrabberIcon isVertical={isVertical} color={color} />
        </div>
      </Render.If>
    </>
  );
};

export default ResizeGrabber;
